import { ReactNode, useMemo } from 'react';

import {
  useGetAlertsByMachineIdQuery,
  useGetMachineDataScienceAlertsQuery,
  useGetMachineOverviewAlarmsQuery
} from 'api';
import { useTranslation } from 'react-i18next';
import { TableRow } from 'components/machine-health/IssuesTable';

// Types
import { Alert, AlertStatus, MachineAlert } from 'types/machine-health/alerts';
import { Alarm } from 'types/machine-health/alarms';
import { getDurationBetweenTimestamps } from 'common/helpers/v1';
import { ActiveIssuesTableDataItemProps } from 'common/ui/ActiveIssuesTable/ActiveIssuesTable';

interface ActiveIssuesTableProps {
  machineId: string;
}

export const useMachineCardWidget = ({
  machineId,
  pollingInterval = 120000
}: ActiveIssuesTableProps): {
  activeIssues?: boolean;
  isLoading?: boolean;
  hasError?: ReactNode;
} => {
  const { t } = useTranslation(['mh', 'common']);

  const {
    data: alarmsData,
    isLoading: alarmsLoading,
    error: alarmsError
  } = useGetMachineOverviewAlarmsQuery({ machineId }, { pollingInterval });
  const {
    data: alertsData,
    isLoading: alertsLoading,
    error: alertsError
  } = useGetMachineDataScienceAlertsQuery({
    machineId,
    status: AlertStatus.NotComplete
  }, { pollingInterval });
  const {
    data: machineAlertsData,
    error: machineAlertsError,
    isLoading: machineAlertsLoading
  } = useGetAlertsByMachineIdQuery({
    machineId,
    active: true
  }, { pollingInterval });

  const excludeHiddenAlarms = alarmsData?.filter(
    (alarm) => alarm?.location?.trim().toLocaleLowerCase() !== 'hidden'
  );

  const activeIssues = useMemo(() => {
    const alarmRows = excludeHiddenAlarms || [];
    const alertRows = alertsData || [];
    const machineAlertRows = machineAlertsData || [];
    const combinedRows = [...alarmRows, ...alertRows, ...machineAlertRows];

    return combinedRows.length > 0;
  }, [alarmsData, alertsData, machineAlertsData]);

  return {
    activeIssues: activeIssues,
    isLoading: alarmsLoading || alertsLoading || machineAlertsLoading,
    hasError: alarmsError || alertsError || machineAlertsError
  };
};
